/**
 * Runtime sanity checks for confidence outputs.
 *
 * The compute engine is pure math, but bad inputs (a mis-scaled Big Five
 * value, a NaN consensus, a future timestamp from a clock-skewed client)
 * can still yield an implausible `ConfidenceReport`. This module inspects
 * a finished report and flags anything that shouldn't reach the UI, so
 * callers can render a friendly fallback instead of a nonsense percent.
 *
 * The checks are intentionally conservative — we only flag things that
 * are clearly wrong (out of range, NaN, contradictory band vs value,
 * future timestamps, everything-max-with-one-source). Borderline reports
 * pass through untouched.
 */

import { DEFAULT_BAND_THRESHOLDS, resolveBand } from './bands';
import type { ConfidenceBand, ConfidenceReport } from './types';

export type SanityCode =
  | 'value_out_of_range'
  | 'value_not_finite'
  | 'band_mismatch'
  | 'contributor_out_of_range'
  | 'value_without_sources'
  | 'future_timestamp'
  | 'implausible_high';

export interface SanityIssue {
  code: SanityCode;
  message: string;
}

export interface SanityResult {
  ok: boolean;
  issues: SanityIssue[];
  /**
   * A safe report the UI can render. When issues are present this is
   * downgraded to `insufficient` so we never surface a bogus percent.
   */
  safe: ConfidenceReport;
}

const CONTRIB_KEYS = [
  'consensus',
  'coverage',
  'recency',
  'agreement',
  'dispute',
] as const;

function inUnitRange(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1;
}

/**
 * Validate a `ConfidenceReport` and return a UI-safe version.
 * Never throws.
 */
export function checkConfidenceReport(
  report: ConfidenceReport | null | undefined,
): SanityResult {
  const issues: SanityIssue[] = [];

  if (!report) {
    return {
      ok: true,
      issues,
      safe: {
        value: null,
        band: 'insufficient',
        nSources: 0,
        lastUpdated: null,
        contributors: {},
        reason: 'No confidence data available yet.',
      },
    };
  }

  const { value, band, nSources, contributors, lastUpdated } = report;

  if (value !== null) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      issues.push({
        code: 'value_not_finite',
        message: `Confidence value is not a finite number (${String(value)}).`,
      });
    } else if (value < 0 || value > 1) {
      issues.push({
        code: 'value_out_of_range',
        message: `Confidence value ${value} outside [0, 1].`,
      });
    } else {
      const expectedBand = resolveBand(value, DEFAULT_BAND_THRESHOLDS);
      // Only complain when the mismatch spans more than one band tier —
      // per-framework calibration can legitimately shift adjacent bands.
      if (bandDistance(expectedBand, band) > 1) {
        issues.push({
          code: 'band_mismatch',
          message: `Band '${band}' inconsistent with value ${value.toFixed(2)} (expected '${expectedBand}').`,
        });
      }
    }

    if (nSources <= 0) {
      issues.push({
        code: 'value_without_sources',
        message: 'Confidence value emitted with zero sources.',
      });
    }

    // "Everything maxed with one source" is almost always a bug in the
    // pipeline (e.g. contributors defaulted to 1.0). Real high-confidence
    // reports need at least two agreeing sources.
    if (
      typeof value === 'number' &&
      value >= 0.95 &&
      nSources < 2 &&
      (contributors?.coverage ?? 0) >= 0.95
    ) {
      issues.push({
        code: 'implausible_high',
        message: 'Near-maximum confidence from a single source is not plausible.',
      });
    }
  }

  if (contributors) {
    for (const k of CONTRIB_KEYS) {
      const v = contributors[k];
      if (v === undefined) continue;
      if (!inUnitRange(v)) {
        issues.push({
          code: 'contributor_out_of_range',
          message: `Contributor '${k}' = ${String(v)} outside [0, 1].`,
        });
      }
    }
  }

  if (lastUpdated) {
    const ts = Date.parse(lastUpdated);
    if (!Number.isNaN(ts) && ts - Date.now() > 60 * 60 * 1000) {
      issues.push({
        code: 'future_timestamp',
        message: 'lastUpdated is in the future — clock skew or bad data.',
      });
    }
  }

  if (issues.length === 0) {
    return { ok: true, issues, safe: report };
  }

  // Log once per unique issue set so we can catch regressions in the wild
  // without spamming the console on every re-render.
  logIssuesOnce(issues);

  return {
    ok: false,
    issues,
    safe: {
      value: null,
      band: 'insufficient',
      nSources: Math.max(0, nSources | 0),
      lastUpdated: lastUpdated ?? null,
      contributors: {},
      reason: friendlyFallbackReason(issues),
    },
  };
}

const BAND_ORDER: ConfidenceBand[] = ['insufficient', 'low', 'medium', 'high'];
function bandDistance(a: ConfidenceBand, b: ConfidenceBand): number {
  return Math.abs(BAND_ORDER.indexOf(a) - BAND_ORDER.indexOf(b));
}

export function friendlyFallbackReason(issues: SanityIssue[]): string {
  if (issues.length === 0) return 'Confidence unavailable.';
  // User-friendly message — details go to the console for engineers.
  return "We couldn't verify this confidence score, so we're hiding it until the underlying data is fixed.";
}

const seen = new Set<string>();
function logIssuesOnce(issues: SanityIssue[]) {
  if (typeof console === 'undefined') return;
  const key = issues.map((i) => i.code).sort().join('|');
  if (seen.has(key)) return;
  seen.add(key);
  // eslint-disable-next-line no-console
  console.warn('[confidence] implausible report suppressed:', issues);
}

/** Test-only helper: reset the dedupe cache. */
export function __resetSanityLogCache() {
  seen.clear();
}